'use client';

import { useTranslations } from 'next-intl';
import { BaseLineSort } from '@/app/components/icons/BaseLineSort';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import type { OrderBy, SortBy } from '../types';

interface SortDropdownProps {
  sortBy: SortBy;
  order: OrderBy;
  onSortByChange: (value: SortBy) => void;
  onOrderChange: (value: OrderBy) => void;
}

export const SortDropdown = ({ sortBy, order, onSortByChange, onOrderChange }: SortDropdownProps) => {
  const t = useTranslations();

  const sortByOptions: { label: string; value: NonNullable<SortBy> }[] = [
    { label: t('creations.sort.lastOpened'), value: 'last_open_at' },
    { label: t('creations.sort.dateCreated'), value: 'created_at' },
  ];

  const orderOptions: { label: string; value: NonNullable<OrderBy> }[] = [
    { label: t('creations.sort.newest'), value: 'newest' },
    { label: t('creations.sort.oldest'), value: 'oldest' },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div>
          <CustomTooltip content={t('creations.sort.sortBy')}>
            <div className="flex h-7 items-center rounded-8 bg-system-white10 px-2 aria-expanded:bg-system-white24">
              <BaseLineSort className="text-base" />
            </div>
          </CustomTooltip>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="border border-system-white12 bg-[#1A1A1A]">
        <DropdownMenuLabel className="mb-1">{t('creations.sort.sortBy')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={sortBy}
          onValueChange={(value) => onSortByChange(value as SortBy)}
          className="space-y-1"
        >
          {sortByOptions.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>

        {sortBy === 'created_at' && (
          <>
            <DropdownMenuLabel className="my-1">{t('creations.sort.order')}</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={order}
              onValueChange={(value) => onOrderChange(value as OrderBy)}
              className="space-y-1"
            >
              {orderOptions.map((option) => (
                <DropdownMenuRadioItem key={option.value} value={option.value}>
                  {option.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
